import {
  CheckCircle2,
  Circle,
  Flag,
  Loader2,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import { Fragment } from "react";
import { cn } from "@/lib/utils";
import {
  SectionStatus,
  type VerificationSection,
  type VerificationSectionId,
} from "./verification-sections";
import { SectionNavSkeletonRows } from "./verification-report-skeleton";

const STATUS_ICON: Record<
  SectionStatus,
  { icon: LucideIcon; label: string; className: string }
> = {
  [SectionStatus.Concern]: {
    icon: Flag,
    label: "Concern",
    className: "text-red-600",
  },
  [SectionStatus.NeedsAttention]: {
    icon: TriangleAlert,
    label: "Needs attention",
    className: "text-amber-600",
  },
  [SectionStatus.Collecting]: {
    icon: Loader2,
    label: "Collecting results",
    className: "text-blue-600 motion-safe:animate-spin",
  },
  [SectionStatus.Open]: {
    icon: Circle,
    label: "Awaiting review",
    className: "text-slate-400",
  },
  [SectionStatus.Accepted]: {
    icon: CheckCircle2,
    label: "All items accepted",
    className: "text-green-600",
  },
};

export type SectionNavEntry = {
  section: VerificationSection;
  /** Absent for entries that summarize no items. */
  status?: SectionStatus;
};

/** The left menu: a vertical card on wide screens, a scrolling row on narrow
 * ones. */
export function SectionNav({
  entries,
  selected,
  itemsLoading,
  onSelect,
}: {
  entries: SectionNavEntry[];
  /** Placeholders between the first and last entry while the item sections
   * are not known yet. */
  itemsLoading: boolean;
  selected: VerificationSectionId;
  onSelect: (id: VerificationSectionId) => void;
}) {
  // `relative` makes the nav the containing block of its screen-reader-only
  // status text, which is absolutely positioned. Otherwise that text escapes
  // the nav's horizontal scroll on mobile and widens the whole page.
  return (
    <nav
      aria-label="Verification sections"
      className="relative flex shrink-0 overflow-x-auto border-b lg:w-60 lg:flex-col lg:overflow-visible lg:border-b-0 lg:border-r"
    >
      {entries.map(({ section, status }, index) => {
        const isSelected = section.id === selected;
        const presentation = status ? STATUS_ICON[status] : null;
        const Icon = presentation?.icon;
        return (
          <Fragment key={section.id}>
            {itemsLoading && index === entries.length - 1 && (
              <SectionNavSkeletonRows />
            )}
            <button
              type="button"
              aria-current={isSelected ? "page" : undefined}
              className={cn(
                "flex items-center justify-between gap-3 whitespace-nowrap border-b-2 px-4 py-3 text-left text-sm transition-colors hover:bg-slate-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary lg:border-b-0 lg:border-l-2",
                isSelected
                  ? "border-primary bg-primary/5 font-semibold text-primary"
                  : "border-transparent text-foreground",
              )}
              onClick={() => onSelect(section.id)}
            >
              {section.label}
              {Icon && presentation && (
                <>
                  <Icon
                    className={cn("size-4 shrink-0", presentation.className)}
                    aria-hidden
                  />
                  <span className="sr-only">{presentation.label}</span>
                </>
              )}
            </button>
          </Fragment>
        );
      })}
    </nav>
  );
}
